import React from "react";
import SEO from "../components/SEO";
import LegalShell from "../components/LegalShell";

/**
 * Contrat de fourniture de messagerie sécurisée de santé (MSSanté) — v1
 * Page utilisée comme support légal du checkout Stripe (checkbox d'acceptation).
 * NON listée dans le menu/footer public — accessible uniquement via le lien
 * dans le wizard de souscription.
 *
 * Coordonnées : METACONNECT.FR SASU — SIRET 931 999 833 00011 — TVA FR22931999833
 * Représentant : Benoît Tommasino — Siège : 68 route de la Ferté-Saint-Aubin, 45150 Jargeau
 */
export default function Contrat() {
  return (
    <LegalShell
      eyebrow="Contrat MSSanté · v1"
      title="Contrat de fourniture de messagerie sécurisée de santé (MSSanté)"
      lastUpdate="2 mai 2026"
      testid="page-contrat"
    >
      <SEO
        title="Contrat MSSanté · MetaConnect"
        description="Contrat de fourniture de messagerie sécurisée de santé MSSanté entre MetaConnect.fr SASU et le client professionnel."
        canonical="/contrat"
      />

      <p className="legal-note">
        <strong>Préambule&nbsp;:</strong> le présent contrat encadre la fourniture par MetaConnect
        d'un service de messagerie sécurisée de santé (MSSanté) à un Client professionnel. Il est
        réputé accepté par le Client lors de la validation de la souscription en ligne (case à
        cocher), avec horodatage et conservation de la version acceptée.
      </p>

      <h2>1. Parties</h2>
      <p>
        <strong>Entre&nbsp;:</strong>
      </p>
      <p>
        <strong>METACONNECT.FR</strong> – Société par Actions Simplifiée Unipersonnelle (SASU)
        au capital de 1&nbsp;000&nbsp;€ – SIREN&nbsp;: 931 999 833 – SIRET&nbsp;: 931 999 833 00011 – N°&nbsp;TVA intracommunautaire&nbsp;:
        FR22 931 999 833 – Siège social&nbsp;: 68 route de la Ferté-Saint-Aubin, 45150 Jargeau,
        France – Représentée par Monsieur Benoît Tommasino, Président – Email&nbsp;:{" "}
        <a href="mailto:contact@metaconnect.fr">contact@metaconnect.fr</a>. Ci-après «&nbsp;le Prestataire&nbsp;».
      </p>
      <p>
        <strong>Et&nbsp;:</strong> la société Cliente identifiée lors de la souscription
        (ci-après «&nbsp;le Client&nbsp;»), dont la raison sociale, le numéro SIRET, l'adresse et
        le nom du représentant légal sont renseignés au moment de la création de son compte
        professionnel et de la BAL MSSanté.
      </p>

      <h2>2. Objet</h2>
      <p>
        Le présent contrat a pour objet la mise à disposition d'un service de messagerie sécurisée
        de santé (MSSanté) permettant l'échange sécurisé de données de santé entre professionnels
        habilités. Ce service s'inscrit dans le cadre de l'Espace de Confiance MSSanté et respecte
        les référentiels en vigueur de l'Agence du Numérique en Santé (ANS).
      </p>
      <p>
        Le Prestataire agit en qualité de partenaire distributeur d'une solution de messagerie
        sécurisée de santé opérée par <strong>Maincare Solutions</strong> (groupe La Poste), et
        assure à ce titre l'intégration, le déploiement, l'accompagnement et le support auprès du
        Client.
      </p>

      <h2>3. Description du service</h2>
      <ul>
        <li>Mise à disposition de boîtes MSSanté</li>
        <li>Organisation des boîtes par lieu physique</li>
        <li>Accès à un espace de gestion (Super Administrateur)</li>
        <li>Intégration dans l'annuaire santé</li>
      </ul>

      <h2>4. Organisation des boîtes</h2>
      <ul>
        <li>1 boîte par lieu physique</li>
        <li>Rattachement de plusieurs professionnels à une même boîte</li>
        <li>Gestion des délégations (secrétariat, orthoptistes, etc.)</li>
        <li>Le responsable de la boîte peut déléguer l'accès sous sa responsabilité</li>
      </ul>

      <h2>5. Administration</h2>
      <p>Un accès Super Administrateur est fourni permettant&nbsp;:</p>
      <ul>
        <li>création des boîtes</li>
        <li>gestion des utilisateurs</li>
        <li>gestion des accès et délégations</li>
        <li>structuration de l'organisation</li>
      </ul>
      <p>Le Client est responsable de la gestion des accès.</p>

      <h2>6. Annuaire santé</h2>
      <p>Les adresses MSSanté&nbsp;:</p>
      <ul>
        <li>sont automatiquement référencées dans l'annuaire santé</li>
        <li>sont rattachées à l'identité du professionnel ou de la structure</li>
      </ul>
      <p>Ce référencement permet les échanges sécurisés entre professionnels.</p>

      <h2>7. Conditions tarifaires</h2>
      <p>Le service est proposé selon deux formules au choix du Client&nbsp;:</p>
      <ul>
        <li>
          <strong>Mensuel&nbsp;:</strong> 19,99&nbsp;€ HT par boîte aux lettres et par mois.
        </li>
        <li>
          <strong>Annuel&nbsp;:</strong> 199,99&nbsp;€ HT par boîte aux lettres et par an
          (équivalent à environ 16,67&nbsp;€ HT/mois — soit 2 mois offerts par rapport au tarif
          mensuel).
        </li>
      </ul>
      <p>
        TVA&nbsp;20&nbsp;% en sus, applicable selon la réglementation en vigueur. Les tarifs
        s'entendent par boîte aux lettres MSSanté commandée.
      </p>

      <h2>8. Conditions financières</h2>
      <ul>
        <li>Facturation à l'activation des boîtes</li>
        <li>Démarrage de l'abonnement à la création</li>
        <li>
          <strong>Paiement par prélèvement automatique SEPA</strong>, mis en place via mandat
          électronique au moment de la souscription
        </li>
      </ul>
      <p>
        Le mandat de prélèvement SEPA est conservé sous forme dématérialisée. En cas de rejet
        de prélèvement, le Prestataire pourra suspendre l'accès au service après mise en
        demeure restée infructueuse.
      </p>

      <h2>9. Obligations du Client</h2>
      <p>Le Client s'engage à&nbsp;:</p>
      <ul>
        <li>respecter les règles MSSanté</li>
        <li>garantir un usage conforme aux échanges de soins</li>
        <li>ne pas utiliser de messageries non sécurisées pour les données de santé</li>
        <li>faire accepter les conditions d'utilisation aux utilisateurs rattachés à ses boîtes</li>
      </ul>
      <p>Les données échangées sont couvertes par le secret professionnel.</p>

      <h2>10. Obligations des utilisateurs</h2>
      <p>Les utilisateurs s'engagent à&nbsp;:</p>
      <ul>
        <li>utiliser la messagerie dans le cadre de la coordination des soins</li>
        <li>ne pas transférer des contenus vers des messageries non sécurisées</li>
        <li>garantir la sécurité de leurs accès</li>
        <li>respecter le RGPD</li>
      </ul>
      <p>Chaque utilisateur est responsable des messages échangés.</p>

      <h2>11. Données de santé &amp; hébergement HDS</h2>
      <p>
        Le service est opéré par Maincare Solutions sur des Datacenters situés en{" "}
        <strong>France métropolitaine</strong> (région Île-de-France et Nouvelle-Aquitaine),{" "}
        <strong>certifiés Hébergeur de Données de Santé (HDS) et ISO 27001</strong>.
      </p>
      <ul>
        <li>Hébergement conforme HDS — données conservées sur le territoire français</li>
        <li>Sécurité conforme aux standards MSSanté et au Référentiel&nbsp;#1 Opérateurs de l'ANS</li>
        <li>Confidentialité des échanges — chiffrement bout-en-bout</li>
      </ul>
      <p>Le Prestataire n'accède pas aux contenus échangés.</p>

      <h2>12. Responsabilité médicale</h2>
      <p>Les professionnels de santé&nbsp;:</p>
      <ul>
        <li>restent seuls responsables des contenus qu'ils émettent ou reçoivent</li>
        <li>conservent leur indépendance professionnelle</li>
        <li>assument leurs décisions médicales</li>
      </ul>

      <h2>13. Sécurité &amp; conformité</h2>
      <ul>
        <li>Authentification forte (CPS / e-CPS / Pro Santé Connect)</li>
        <li>Traçabilité des accès et journalisation conforme PGSSI-S</li>
        <li>Protection des données — politique ISO 27001 chez l'opérateur</li>
        <li>
          Conformité au <strong>Référentiel&nbsp;#1 Opérateurs de Messageries Sécurisées de Santé</strong>
          {" "}publié par l'Agence du Numérique en Santé (ANS)
        </li>
      </ul>

      <h2>14. Protection des données personnelles (RGPD)</h2>
      <p>
        Dans le cadre de la fourniture du service, le Client est <strong>Responsable de
        traitement</strong>, le Prestataire agit en qualité de <strong>Sous-traitant</strong> au
        sens du RGPD, et Maincare Solutions intervient comme <strong>sous-traitant ultérieur</strong>.
        Les Parties s'engagent à respecter le Règlement (UE) 2016/679 (RGPD) et la loi
        Informatique et Libertés.
      </p>
      <ul>
        <li>
          <strong>Notification de violation&nbsp;: 72&nbsp;heures.</strong> En cas de violation
          de données à caractère personnel, le Prestataire (et Maincare en tant que sous-traitant
          ultérieur) notifie le Client dans un délai maximum de 72&nbsp;heures à partir des
          premiers éléments constatés (article 33 RGPD).
        </li>
        <li>Aucun transfert de données hors Espace Économique Européen n'est prévu.</li>
        <li>
          DPO de Maincare Solutions&nbsp;: Christophe Demignot —{" "}
          <a href="mailto:dpo@maincare.fr">dpo@maincare.fr</a>.
        </li>
      </ul>

      <h2>15. Audits ANS</h2>
      <p>
        L'Agence du Numérique en Santé peut, dans le cadre du Référentiel&nbsp;#1, effectuer des
        contrôles de conformité du service MSSanté. Le Client s'engage à coopérer raisonnablement
        si un tel audit nécessite des informations relevant de son périmètre.
      </p>

      <h2>16. Durée</h2>
      <ul>
        <li>
          <strong>Engagement initial&nbsp;: 12 mois</strong> à compter de l'activation effective
          de la première BAL souscrite
        </li>
        <li>
          <strong>Reconduction tacite</strong> par périodes successives de 12 mois, sauf
          résiliation dans les conditions de l'article 15
        </li>
      </ul>

      <h2>17. Résiliation</h2>
      <p>
        <strong>Préavis de résiliation&nbsp;: 3 mois</strong> avant la date d'échéance annuelle.
        À défaut de résiliation notifiée dans ce délai, le contrat est tacitement reconduit pour
        12 mois supplémentaires.
      </p>
      <p>
        La résiliation peut être effectuée par le Client&nbsp;:
      </p>
      <ul>
        <li>
          via l'espace de gestion en ligne (Customer Portal Stripe), avec prise d'effet à la fin
          de la période d'engagement en cours dans le respect du préavis ci-dessus&nbsp;;
        </li>
        <li>
          par courrier électronique adressé à <a href="mailto:contact@metaconnect.fr">contact@metaconnect.fr</a>.
        </li>
      </ul>
      <p>
        Le Prestataire pourra suspendre ou résilier le service en cas de non-paiement persistant,
        après mise en demeure restée sans effet pendant 30 jours.
      </p>

      <h2>18. Limitation de responsabilité</h2>
      <p>Le Prestataire ne pourra être tenu responsable&nbsp;:</p>
      <ul>
        <li>des usages des professionnels rattachés au compte du Client</li>
        <li>des contenus échangés via les boîtes MSSanté</li>
        <li>
          des interruptions externes (réseau, infrastructure de l'opérateur Maincare/La Poste,
          référentiels nationaux ANS, etc.)
        </li>
      </ul>

      <h2>19. Acceptation</h2>
      <p>L'acceptation du présent contrat par le Client (case à cocher en ligne) vaut&nbsp;:</p>
      <ul>
        <li>acceptation du service</li>
        <li>engagement à respecter les règles MSSanté</li>
        <li>validation des conditions tarifaires (article 7) et financières (article 8)</li>
      </ul>
      <p>
        La date et l'horodatage de l'acceptation, l'identité du représentant légal du Client et
        l'adresse IP de connexion sont conservés à titre probatoire pendant la durée légale.
      </p>

      <h2>20. Loi applicable et juridiction compétente</h2>
      <p>
        Le présent contrat est soumis au <strong>droit français</strong>. À défaut de résolution
        amiable, tout litige relatif à sa formation, son interprétation ou son exécution sera
        de la compétence exclusive du <strong>Tribunal de commerce d'Orléans</strong>.
      </p>

      <p className="legal-note" style={{ marginTop: "2rem" }}>
        <strong>Version&nbsp;:</strong> v1 — 2 mai 2026.<br />
        <strong>Conservation&nbsp;:</strong> chaque acceptation est archivée avec horodatage,
        identifiant client et version contractuelle.
      </p>
    </LegalShell>
  );
}
